import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import styles from './Reveal.module.css'

type RevealProps = {
  children: ReactNode
  /** Stagger, in ms, for items revealed as a group. */
  delay?: number
  className?: string
}

/**
 * Fades content up as it scrolls into view.
 *
 * Content is always in the DOM and always readable — the effect only touches
 * opacity and transform, and is skipped entirely for reduced-motion visitors.
 */
export function Reveal({ children, delay = 0, className }: RevealProps) {
  const reducedMotion = usePrefersReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (reducedMotion) {
      setShown(true)
      return
    }

    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true)
            observer.disconnect()
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [reducedMotion])

  return (
    <div
      ref={ref}
      className={`${styles.reveal} ${className ?? ''}`}
      data-shown={shown}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}
